import { useState } from "react";
import type { FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahghvboh653ivfvnujg";

const types = ["爆料投稿", "内容纠错", "合作咨询", "其他事项"];

interface FormState {
  status: "idle" | "submitting" | "success" | "error";
  message: string;
}

export default function ContactForm() {
  const [state, setState] = useState<FormState>({ status: "idle", message: "" });

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setState({ status: "success", message: "已收到你的信息，我们会在工作日内回复。" });
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      const text = typeof value === "string" ? value.trim() : "";
      if (text) payload.append(key, text);
    });

    setState({ status: "submitting", message: "" });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam|form data is spam/i.test(serverMsg);
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setState({ status: "success", message: "已收到你的信息，我们会在工作日内回复。" });
        form.reset();
      } else {
        setState({
          status: "error",
          message: isSpam
            ? "提交未通过校验，请检查内容后重试。"
            : serverMsg || "提交失败，请稍后再试。",
        });
      }
    } catch {
      setState({ status: "error", message: "网络异常，提交失败，请稍后再试。" });
    }
  };

  return (
    <form
      data-readdy-form
      id="contact-form"
      onSubmit={onSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">提交线索或留言</h2>
      <p className="mt-2 text-sm text-foreground-600">
        描述越具体越好，建议写清时间、地点和你亲眼看到的部分。带 * 的为必填项。
      </p>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="cf-name" className="text-sm font-medium text-foreground-800">
            你的称呼 *
          </label>
          <input
            id="cf-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="cf-email" className="text-sm font-medium text-foreground-800">
            联系邮箱 *
          </label>
          <input
            id="cf-email"
            name="email"
            type="email"
            required
            placeholder="方便回复的邮箱"
            className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="cf-type" className="text-sm font-medium text-foreground-800">
            事项类型
          </label>
          <select
            id="cf-type"
            name="type"
            defaultValue={types[0]}
            className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
          >
            {types.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="cf-subject" className="text-sm font-medium text-foreground-800">
            线索标题
          </label>
          <input
            id="cf-subject"
            name="subject"
            type="text"
            placeholder="一句话概括这条线索"
            className="rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-2">
        <label htmlFor="cf-message" className="text-sm font-medium text-foreground-800">
          详细内容 *
        </label>
        <textarea
          id="cf-message"
          name="message"
          required
          maxLength={500}
          rows={6}
          placeholder="尽量写清时间、地点、经过，以及你的信息来源（不超过 500 字）"
          className="resize-none rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
        <span className="text-xs text-foreground-500">最多 500 字，请勿包含他人隐私信息。</span>
      </div>

      <div className="form-alt-contact">
        <label htmlFor="cf-contact-alt">备用联系方式</label>
        <input
          id="cf-contact-alt"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      {state.message ? (
        <p
          role="status"
          className={`mt-5 rounded-md px-4 py-3 text-sm ${
            state.status === "success"
              ? "bg-secondary-100 text-secondary-900"
              : "bg-primary-50 text-primary-800"
          }`}
        >
          {state.message}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={state.status === "submitting"}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        <i className={state.status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
        {state.status === "submitting" ? "正在提交…" : "提交线索"}
      </button>
    </form>
  );
}